import { createRef, useEffect, useRef } from 'react';
import cn from 'classnames';
import { ProductCategoryConditionOption } from '@lambda/apis/src/category/types';

export default function ConditionTab({
  newCondition,
  sliderPreset,
  selectedIndex,
  isOpen,
  setSelectedIndex,
  setSetCurrentActiveCondition,
}: {
  newCondition: ProductCategoryConditionOption[];
  sliderPreset: boolean;
  selectedIndex: number;
  isOpen: boolean;
  setSelectedIndex: (index: number) => void;
  setSetCurrentActiveCondition: (condition: string) => void;
}) {
  const conditionRefs = useRef(new Array(newCondition.length));

  conditionRefs.current = newCondition.map(
    (_element, i) => conditionRefs.current[i] ?? createRef(),
  );

  useEffect(() => {
    // to auto scroll into view for the selected condition, since there is slider in mobile view
    if (
      isOpen &&
      selectedIndex &&
      conditionRefs.current[selectedIndex]?.current != null
    ) {
      conditionRefs.current[selectedIndex].current.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [conditionRefs.current]);

  return (
    <div
      className={cn(
        'hide-scrollbar w-full shrink-0 py-5',
        {
          'flex grid-cols-5 flex-nowrap gap-x-0 overflow-x-auto xs:grid xs:gap-x-3 xs:overflow-x-hidden [&>*:not(:last-child)]:mr-2 [&>*:not(:last-child)]:xs:mr-0 [&>*]:min-w-[80px] [&>*]:xxs:min-w-[100px] [&>*]:xs:min-w-full [&>*]:2xl:min-w-[80px]':
            newCondition.length === 5,
        },
        {
          '[&>*]:xs:min-min-w-[100px] grid grid-cols-3 gap-x-2 xs:gap-x-3 [&>*]:min-w-full':
            newCondition.length === 3,
        },
        {
          '[&>*]:xs:min-min-w-[100px] grid grid-cols-4 gap-x-2 xs:gap-x-3 [&>*]:min-w-full':
            newCondition.length === 4,
        },
        {
          'flex flex-nowrap gap-x-0 overflow-x-auto sm:grid sm:grid-cols-6 sm:gap-x-2 [&>*:not(:last-child)]:mr-2 [&>*:not(:last-child)]:xs:mr-3 [&>*]:min-w-[80px] [&>*]:xxs:min-w-[100px] [&>*]:sm:min-w-full':
            newCondition.length === 6,
        },
        {
          'mt-[10vh]': !sliderPreset,
        },
      )}
    >
      {newCondition.map((conditionTab, tabIndex) => {
        const selected = tabIndex === selectedIndex;

        return (
          <div
            key={`${conditionTab.name}`}
            className={cn(
              'relative mb-0 flex min-h-full w-full min-w-full items-center justify-center rounded-md border border-gray-500 py-5 text-xs leading-5 text-gray-700 ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 hover:cursor-pointer hover:border-gray-700 hover:font-bold focus:outline-none focus:ring-2',
              {
                '!border-gray-700 bg-white font-bold': selected,
              },
              {
                'font-normal': !selected,
              },
            )}
            ref={conditionRefs.current[tabIndex]}
            onClick={() => {
              setSelectedIndex(tabIndex);
              setSetCurrentActiveCondition(newCondition[tabIndex].name);
            }}
          >
            <p className="text-center">{conditionTab.name}</p>
          </div>
        );
      })}
    </div>
  );
}
